/**
 * 給与計算用の契約テーブル。行 = worker、列 = 雇用契約 DB の給与計算関連項目。
 *
 * 列の一次ソースは payroll item カタログ（`payrollItemCatalog.ts`）の `category: 'reference'`。
 * 供給元が雇用契約（労働条件）の R011 / R012 / R013 / R015 を全て列として持ち、各列は
 * `payrollItemId` でカタログを名指しする。表示用の `code` は利用者が編集し得るので
 * 永続化せず、コメントでの言及にとどめる。
 *
 * R040 基本給（id `pi-base-salary`）は `category: 'reference'` だが、この表の基本給列には
 * 当てていない。カタログの説明が「基本給・基準賃金の**計算によって得られる**固定支給項目」で、
 * 供給元区分でもどのコア DB にも割り当てられていない（People / 申告データ / 雇用契約 /
 * 勤怠 / 事業所 / Run のいずれにも属さない）。つまり R040 は給与計算 Run が算出する
 * 支給項目側の値であり、雇用契約が定める月額基本給とは別物。ここで結び付けると
 * 「契約に書いてある額」と「計算結果」が同一項目に見えてしまうため、あえて紐づけない。
 *
 * 列順は 氏名 / 社員番号 → payroll item を code 昇順 → payroll item に無い実装済み項目 →
 * モック列 の順。カタログとの突合を目で追えるようにするため、読みやすさより code 順を優先している。
 *
 * 中盤の実装済み項目（基本給・賃金形態・各単価・役職手当・固定残業手当と時間・
 * 所定労働時間（1日）・支給サイクル・契約期間・昇給後基本給の適用日）は payroll item に
 * 無いが給与計算に要る項目で、給与計算入力スナップショット（`payrollInputSnapshots.ts` の
 * `CONTRACT_SEEDS`）と 1 対 1 で対応し、`sourcePath` も同じ表記を使っている。
 *
 * 末尾は雇用契約 DB が未実装の項目で、**このテーブル内だけのモック列**。契約側の実装が
 * 追いつくまで給与計算が何を読む必要があるかを示すために置いており、契約ドメインのコードには
 * 存在しない。モック列は `payrollItemId` も `sourcePath` も持たせていない（カタログ上の項目でも
 * コア DB 上の在り処でもないため、持たせると実装済みの列と見分けが付かなくなる）。
 *
 * 時間の列は「時間:分」表記の文字列で持つ（カタログ側の時間項目が分で持つことの表示換算）。
 *
 * 値はサンプル（架空の従業員）。メンバー・勤怠テーブルと同じ 10 名・同じ並び。
 */

import type { PayrollReferenceCatalogTable } from '../../types';

/**
 * 給与計算用の契約テーブル。
 *
 * 値の整合で守っていること:
 * - メンバーテーブルの報酬月額（R041）= 基本給 + 役職手当 + 固定残業手当 + 定期代（R008）。
 *   賃金形態が時給の行は「時間単価 × 勤怠テーブルの所定内労働時間」、日給の行は
 *   「日額単価 × 勤怠テーブルの出勤日数（R025）」を基本給の代わりに置いて同じ式が成り立つ。
 * - 基本給は照会先カタログの号俸表（等級 × 号俸）の額と一致する。号俸表を持たない
 *   管理職（メンバーテーブルの役職が部長・課長）と時給・日給の行は表外の額または「—」。
 * - 役職手当は照会先カタログの役職手当 単価表の額と一致する（役職なしの行は 0）。
 * - 賃金形態が「月給」の行だけ基本給を持ち、「時給」は時間単価、「日給」は日額単価を持つ
 *   （持たない単価は「—」）。
 * - 所定労働時間（1日）× 勤怠テーブルの出勤日数 − 遅刻早退時間 = 勤怠テーブルの所定内労働時間。
 * - 週所定労働時間 = 所定労働時間（1日）× 週所定労働日数。
 * - 固定残業手当は「（基本給 + 役職手当）÷ 月平均所定労働時間 × 1.25 × 固定残業時間」に
 *   おおよそ一致する額を置いている。固定残業時間は勤怠テーブルのみなし残業時間（R024）と
 *   同値で、「固定残業時間の超過分」の基準値でもある。定めが無い行は「—」。
 * - 勤務時間制度区分（R015）が「変形労働時間制」の行（EMP-1006 / EMP-1008）だけ、
 *   勤怠テーブルの残業時間（R023）と時間外労働時間（制度反映 R038）が食い違う。
 *   変形の単位（1か月 / 1年）はモック列の「変形労働時間制の区分」が持つ。
 * - 管理監督者に該当する行（EMP-1005）は時間外割増の対象外なので 36協定の上限を持たず、
 *   勤怠テーブルの時間外労働時間も 0:00。
 * - 36協定の上限は勤怠テーブルの時間外労働時間（制度反映 R038）を上回る。月 66:00 の
 *   EMP-1006 だけ特別条項付き（月 100:00 / 年 720:00）にしてある。
 * - 退職日（R013）は在籍中の行では「—」。有期契約の EMP-1004 だけ契約満了に伴う
 *   退職予定日を持ち、契約期間の終期と一致させている。
 * - 入社日（R012）は通算の在籍開始日で、契約期間の始期とは一致しないことがある。
 *   定年後再雇用の EMP-1004 は入社日 1993-04-01 に対し現契約が 2024-10-01 開始。
 *   勤続年数の判定に契約期間を使うと誤るため、この 2 列は別項目として持つ。
 */
export const payrollReferenceContractTable: PayrollReferenceCatalogTable = {
  columns: [{
    label: '氏名',
    sourcePath: '雇用契約 › 当事者 › 氏名'
  }, {
    label: '社員番号',
    sourcePath: '雇用契約 › 当事者 › 従業員番号'
  },
  // R011
  {
    label: '雇用区分',
    payrollItemId: 'pi-r011',
    sourcePath: '雇用契約 › 基本情報 › 雇用区分'
  },
  // R012
  {
    label: '入社日',
    payrollItemId: 'pi-r012',
    sourcePath: '雇用契約 › 基本情報 › 入社日'
  },
  // R013
  {
    label: '退職日',
    payrollItemId: 'pi-r013',
    sourcePath: '雇用契約 › 基本情報 › 退職日'
  },
  // R015
  {
    label: '勤務時間制度区分',
    payrollItemId: 'pi-r015',
    sourcePath: '雇用契約 › 労働条件 › 勤務時間制度区分'
  },
  // ここから下は payroll item カタログに無いが給与計算に要る項目
  // （入力スナップショットの `CONTRACT_SEEDS` と 1 対 1）。
  // 基本給に R040 を当てない理由はファイル冒頭の JSDoc を参照。
  {
    align: 'end',
    label: '基本給（円）',
    sourcePath: '雇用契約 › 報酬 › 基本給'
  }, {
    label: '賃金形態',
    sourcePath: '雇用契約 › 報酬 › 賃金形態'
  }, {
    align: 'end',
    label: '時間単価（円）',
    sourcePath: '雇用契約 › 報酬 › 時間単価'
  }, {
    align: 'end',
    label: '日額単価（円）',
    sourcePath: '雇用契約 › 報酬 › 日額単価'
  }, {
    align: 'end',
    label: '役職手当（円）',
    sourcePath: '雇用契約 › 報酬 › 役職手当'
  }, {
    align: 'end',
    label: '固定残業手当（円）',
    sourcePath: '雇用契約 › 報酬 › 固定残業手当'
  }, {
    align: 'end',
    label: '固定残業時間（時間:分）',
    sourcePath: '雇用契約 › 労働条件 › 固定残業時間'
  }, {
    align: 'end',
    label: '所定労働時間（1日）（時間:分）',
    sourcePath: '雇用契約 › 労働条件 › 所定労働時間（1日）'
  }, {
    label: '支給サイクル',
    sourcePath: '雇用契約 › 報酬 › 支給サイクル'
  }, {
    label: '契約期間',
    sourcePath: '雇用契約 › 基本情報 › 契約期間'
  }, {
    label: '昇給後基本給の適用日',
    sourcePath: '雇用契約 › 履歴 › 改定適用日'
  },
  // ここから下は雇用契約 DB 未実装のためこのテーブル内のモック列。
  // 契約ドメインのコードには対応する項目が無いため
  // `payrollItemId` も `sourcePath` も持たせていない。
  {
    label: '契約種別（モック）'
  }, {
    align: 'end',
    label: '週所定労働日数（日）（モック）'
  }, {
    align: 'end',
    label: '週所定労働時間（時間:分）（モック）'
  }, {
    label: '試用期間（モック）'
  }, {
    label: '変形労働時間制の区分（モック）'
  }, {
    label: '管理監督者区分（モック）'
  }, {
    align: 'end',
    label: '36協定の時間外上限（月）（時間:分）（モック）'
  }, {
    align: 'end',
    label: '36協定の時間外上限（年）（時間:分）（モック）'
  }, {
    label: '賃金締切日・賃金支払日（モック）'
  }],
  rows: [['田村 拓真', 'EMP-1001', '正社員', '2021-04-01', '—', '通常の労働時間制', '310,000', '月給', '—', '—', '15,000', '70,000', '30:00', '8:00', '月次', '2021-04-01 〜 期間の定めなし', '2026-04-01', '無期労働契約', '5', '40:00', '3か月（終了済み）', '適用なし', '非該当', '45:00', '360:00', '末日締め・翌月25日払い'], ['岡部 美咲', 'EMP-1002', '正社員（短時間勤務）', '2019-10-01', '—', '通常の労働時間制', '241,000', '月給', '—', '—', '0', '0', '—', '6:00', '月次', '2019-10-01 〜 期間の定めなし', '2026-04-01', '無期労働契約', '5', '30:00', '3か月（終了済み）', '適用なし', '非該当', '45:00', '360:00', '末日締め・翌月25日払い'], ['川島 隆之', 'EMP-1003', '正社員', '2013-04-01', '—', '通常の労働時間制', '420,000', '月給', '—', '—', '45,000', '0', '—', '8:00', '月次', '2013-04-01 〜 期間の定めなし', '2026-04-01', '無期労働契約', '5', '40:00', '3か月（終了済み）', '適用なし', '非該当', '45:00', '360:00', '末日締め・翌月25日払い'], ['大久保 稔', 'EMP-1004', '嘱託（定年後再雇用）', '1993-04-01', '2027-03-31', '通常の労働時間制', '—', '時給', '1,400', '—', '0', '0', '—', '5:00', '月次', '2024-10-01 〜 2027-03-31', '2026-04-01', '有期労働契約（更新あり）', '4', '20:00', 'なし', '適用なし', '非該当', '45:00', '360:00', '末日締め・翌月25日払い'], ['藤原 里美', 'EMP-1005', '正社員', '2010-04-01', '—', '通常の労働時間制', '480,000', '月給', '—', '—', '80,000', '0', '—', '8:00', '月次', '2010-04-01 〜 期間の定めなし', '2026-04-01', '無期労働契約', '5', '40:00', '3か月（終了済み）', '適用なし', '該当（管理監督者）', '—', '—', '末日締め・翌月25日払い'], ['小柳 亮介', 'EMP-1006', '正社員', '2018-04-01', '—', '変形労働時間制', '290,000', '月給', '—', '—', '0', '42,000', '20:00', '8:00', '月次', '2018-04-01 〜 期間の定めなし', '2026-04-01', '無期労働契約', '5', '40:00', '3か月（終了済み）', '1年単位の変形労働時間制', '非該当', '100:00（特別条項）', '720:00（特別条項）', '末日締め・翌月25日払い'], ['中原 千夏', 'EMP-1007', '正社員', '2022-04-01', '—', '通常の労働時間制', '229,000', '月給', '—', '—', '0', '0', '—', '8:00', '月次', '2022-04-01 〜 期間の定めなし', '2026-04-01', '無期労働契約', '5', '40:00', '3か月（終了済み）', '適用なし', '非該当', '45:00', '360:00', '末日締め・翌月25日払い'], ['望月 康平', 'EMP-1008', '正社員', '2016-04-01', '—', '変形労働時間制', '335,500', '月給', '—', '—', '25,000', '0', '—', '8:00', '月次', '2016-04-01 〜 期間の定めなし', '2026-04-01', '無期労働契約', '5', '40:00', '3か月（終了済み）', '1か月単位の変形労働時間制', '非該当', '45:00', '360:00', '末日締め・翌月25日払い'], ['石垣 芽衣', 'EMP-1009', '契約社員（日給制）', '2025-04-01', '—', '通常の労働時間制', '—', '日給', '—', '10,000', '0', '0', '—', '8:00', '月次', '2025-04-01 〜 2027-03-31', '2026-04-01', '有期労働契約（更新あり）', '5', '40:00', '3か月（終了済み）', '適用なし', '非該当', '45:00', '360:00', '末日締め・翌月25日払い'], ['篠原 正和', 'EMP-1010', '正社員', '2015-04-01', '—', '通常の労働時間制', '264,000', '月給', '—', '—', '15,000', '40,000', '20:00', '8:00', '月次', '2015-04-01 〜 期間の定めなし', '2026-04-01', '無期労働契約', '5', '40:00', '3か月（終了済み）', '適用なし', '非該当', '45:00', '360:00', '末日締め・翌月25日払い']]
};